// Convert HSL string like "hsl(145, 28%, 40%)" to hex like "4f8a5e"
export function hslToHex(hslString) {
  const match = hslString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
  if (!match) return hslString.replace('#', '')
  let [, h, s, l] = match.map(Number)
  s /= 100
  l /= 100
  const a = s * Math.min(l, 1 - l)
  const f = (n) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color).toString(16).padStart(2, '0')
  }
  return `${f(0)}${f(8)}${f(4)}`
}

// Design System: Warm, community-friendly color palette
// HSL-based for easy dark mode adaptation
// Audience: 35+ adults — clean, readable, trustworthy

export const theme = {
  colors: {
    // Sage green — primary brand color (community, neighborhood, outdoors)
    primary: {
      50: 'hsl(145, 30%, 96%)',
      100: 'hsl(145, 30%, 90%)',
      200: 'hsl(145, 28%, 80%)',
      300: 'hsl(145, 25%, 65%)',
      400: 'hsl(145, 24%, 50%)',
      500: 'hsl(145, 28%, 40%)',
      600: 'hsl(145, 32%, 33%)',
      700: 'hsl(145, 35%, 27%)',
      800: 'hsl(145, 35%, 21%)',
      900: 'hsl(145, 38%, 15%)',
      950: 'hsl(145, 40%, 10%)',
    },
    // Warm amber accent — friendly, inviting, garage sale vibes
    accent: {
      50: 'hsl(35, 80%, 96%)',
      100: 'hsl(35, 75%, 90%)',
      200: 'hsl(35, 70%, 80%)',
      300: 'hsl(33, 65%, 68%)',
      400: 'hsl(30, 60%, 55%)',
      500: 'hsl(28, 55%, 48%)',
      600: 'hsl(25, 55%, 40%)',
      700: 'hsl(22, 55%, 33%)',
      800: 'hsl(20, 50%, 26%)',
      900: 'hsl(18, 45%, 20%)',
      950: 'hsl(16, 45%, 13%)',
    },
    // Warm neutral grays with slight warm undertone
    surface: {
      50: 'hsl(40, 20%, 98%)',
      100: 'hsl(40, 15%, 95%)',
      200: 'hsl(38, 12%, 90%)',
      300: 'hsl(36, 10%, 82%)',
      400: 'hsl(34, 8%, 64%)',
      500: 'hsl(32, 6%, 46%)',
      600: 'hsl(30, 8%, 36%)',
      700: 'hsl(28, 10%, 28%)',
      800: 'hsl(26, 12%, 20%)',
      900: 'hsl(24, 14%, 14%)',
      950: 'hsl(22, 16%, 9%)',
    },
    // Info blue — soft, trustworthy, informational
    info: {
      50: 'hsl(210, 40%, 96%)',
      100: 'hsl(210, 38%, 90%)',
      200: 'hsl(210, 35%, 82%)',
      300: 'hsl(210, 30%, 68%)',
      400: 'hsl(210, 30%, 55%)',
      500: 'hsl(210, 35%, 45%)',
      600: 'hsl(210, 40%, 38%)',
      700: 'hsl(210, 42%, 30%)',
      800: 'hsl(210, 40%, 24%)',
      900: 'hsl(210, 40%, 17%)',
    },
    // Semantic: success
    green: {
      50: 'hsl(140, 40%, 96%)',
      100: 'hsl(140, 38%, 90%)',
      200: 'hsl(140, 35%, 80%)',
      300: 'hsl(140, 30%, 65%)',
      400: 'hsl(140, 30%, 50%)',
      500: 'hsl(140, 35%, 40%)',
      600: 'hsl(140, 40%, 33%)',
      700: 'hsl(140, 42%, 26%)',
      800: 'hsl(140, 40%, 20%)',
      900: 'hsl(140, 40%, 14%)',
    },
    // Semantic: error/danger
    red: {
      50: 'hsl(0, 60%, 97%)',
      100: 'hsl(0, 55%, 92%)',
      200: 'hsl(0, 50%, 85%)',
      300: 'hsl(0, 48%, 74%)',
      400: 'hsl(0, 55%, 62%)',
      500: 'hsl(0, 60%, 50%)',
      600: 'hsl(0, 65%, 42%)',
      700: 'hsl(0, 65%, 34%)',
      800: 'hsl(0, 60%, 28%)',
      900: 'hsl(0, 55%, 22%)',
    },
    // Semantic: warning
    amber: {
      50: 'hsl(45, 80%, 96%)',
      100: 'hsl(45, 75%, 88%)',
      200: 'hsl(43, 70%, 78%)',
      300: 'hsl(40, 65%, 65%)',
      400: 'hsl(38, 60%, 52%)',
      500: 'hsl(36, 65%, 44%)',
      600: 'hsl(34, 70%, 36%)',
      700: 'hsl(32, 70%, 28%)',
      800: 'hsl(30, 65%, 22%)',
      900: 'hsl(28, 60%, 16%)',
    },
  },

  // Typography scale — readable for 35+ audience
  fontSize: {
    xs: ['0.8125rem', { lineHeight: '1.25rem' }],     // 13px
    sm: ['0.875rem', { lineHeight: '1.375rem' }],      // 14px
    base: ['1rem', { lineHeight: '1.625rem' }],         // 16px — minimum body
    lg: ['1.125rem', { lineHeight: '1.75rem' }],        // 18px
    xl: ['1.25rem', { lineHeight: '1.875rem' }],        // 20px
    '2xl': ['1.5rem', { lineHeight: '2rem' }],          // 24px
    '3xl': ['1.875rem', { lineHeight: '2.375rem' }],    // 30px
    '4xl': ['2.25rem', { lineHeight: '2.75rem' }],      // 36px
    '5xl': ['3rem', { lineHeight: '1.15' }],            // 48px
  },

  // Spacing scale (base-4 system)
  spacing: {
    px: '1px',
    0: '0',
    0.5: '0.125rem',
    1: '0.25rem',
    1.5: '0.375rem',
    2: '0.5rem',
    2.5: '0.625rem',
    3: '0.75rem',
    4: '1rem',
    5: '1.25rem',
    6: '1.5rem',
    7: '1.75rem',
    8: '2rem',
    10: '2.5rem',
    12: '3rem',
    14: '3.5rem',
    16: '4rem',
    20: '5rem',
    24: '6rem',
    32: '8rem',
  },

  // Shadow scale — subtle, warm-toned
  boxShadow: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.04)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.06), 0 1px 2px -1px rgba(0, 0, 0, 0.06)',
    DEFAULT: '0 2px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 8px -2px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.04)',
    lg: '0 8px 16px -4px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
    xl: '0 16px 32px -8px rgba(0, 0, 0, 0.12), 0 8px 16px -8px rgba(0, 0, 0, 0.06)',
    '2xl': '0 24px 48px -12px rgba(0, 0, 0, 0.18)',
    inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.04)',
    none: 'none',
  },

  // Border radius
  borderRadius: {
    none: '0',
    sm: '0.25rem',
    DEFAULT: '0.5rem',
    md: '0.625rem',
    lg: '0.75rem',
    xl: '1rem',
    '2xl': '1.25rem',
    '3xl': '1.5rem',
    full: '9999px',
  },
}
